import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { ExpandArrow } from '../../atoms/ExpandArrow'
import { useFilterSectionContext } from './FilterSectionContext'

const FilterSectionContainer = styled.div<{ $collapsed: boolean }>`
  margin-bottom: 16px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  position: relative;
  transition: all ${({ theme }) => theme.transitions.default};

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.border.default};
  }
`

const FilterSectionHeader = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.medium}
    ${({ theme }) => theme.spacing.large};
  cursor: pointer;
  user-select: none;
  transition: background ${({ theme }) => theme.transitions.default};

  ${({ $collapsed, theme }) =>
    $collapsed &&
    `
    &:hover {
      background: ${theme.colors.background.secondary};
    }
  `}
`

// `touch-action: pan-y` — the drag handle is a 14×14 dot grid that
// frequently lands under a finger trying to scroll the filter list.
// We *let* the browser keep handling vertical scroll on the handle
// itself; the consumer (SortableFilterSections) gates drag activation
// behind a 250ms long-press via TouchSensor, so a quick swipe through
// the handle scrolls naturally while a deliberate hold-then-move
// still picks up the section. The previous `touch-action: none`
// blocked browser scroll on the handle entirely, which is what made
// scrolling through the filter list on iPhone / touchscreen laptops
// reorder the sections instead (Aldo 2026-05).
const DragHandle = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
  color: ${({ theme }) => theme.colors.text.tertiary};
  padding: ${({ theme }) => theme.spacing.xsmall};
  margin-right: ${({ theme }) => theme.spacing.xsmall};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  touch-action: pan-y;

  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:active {
    cursor: grabbing;
  }
`

const FilterSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

const FilterSectionCount = styled.span<{ $active: boolean }>`
  font-size: 10px;
  font-weight: 400;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.text.tertiary};
  margin-left: 3px;
`

const FilterSectionContent = styled.div<{ $collapsed: boolean }>`
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
  padding: ${({ theme }) => theme.spacing.large};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
  position: relative;
  overflow: visible;
  animation: ${({ $collapsed }) =>
    $collapsed ? 'none' : 'slideDown 0.2s ease-out'};

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

interface FilterSectionProps {
  title: string
  icon: React.ReactNode
  count?: number | string
  defaultCollapsed?: boolean
  children: React.ReactNode
  active?: boolean
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  icon,
  count,
  defaultCollapsed = true,
  children,
  active = false,
  dragHandleProps,
  ...props
}) => {
  const [localCollapsed, setLocalCollapsed] = useState(defaultCollapsed)
  const { expandedSections, scrollToSection, onScrollComplete } =
    useFilterSectionContext()
  const containerRef = useRef<HTMLDivElement>(null)

  // Override collapse when context says expand
  const collapsed =
    expandedSections !== null ? !expandedSections.has(title) : localCollapsed

  // When targeted, persist expanded state and scroll into view
  useEffect(() => {
    if (scrollToSection === title) {
      setLocalCollapsed(false)
      if (containerRef.current) {
        containerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
      onScrollComplete()
    }
  }, [scrollToSection, title, onScrollComplete])

  return (
    <FilterSectionContainer
      ref={containerRef}
      $collapsed={collapsed}
      {...props}
    >
      <FilterSectionHeader
        $collapsed={collapsed}
        onClick={() => setLocalCollapsed(!localCollapsed)}
      >
        <FilterSectionTitle>
          {dragHandleProps && (
            <DragHandle
              {...dragHandleProps}
              onClick={(e) => e.stopPropagation()}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <circle cx="9" cy="5" r="1.5" />
                <circle cx="15" cy="5" r="1.5" />
                <circle cx="9" cy="12" r="1.5" />
                <circle cx="15" cy="12" r="1.5" />
                <circle cx="9" cy="19" r="1.5" />
                <circle cx="15" cy="19" r="1.5" />
              </svg>
            </DragHandle>
          )}
          {icon}
          {title}
          {count !== undefined && (
            <FilterSectionCount $active={active}>({count})</FilterSectionCount>
          )}
        </FilterSectionTitle>
        <ExpandArrow $expanded={!collapsed} />
      </FilterSectionHeader>
      <FilterSectionContent $collapsed={collapsed}>
        {children}
      </FilterSectionContent>
    </FilterSectionContainer>
  )
}
